import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import CryptoJS from "crypto-js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ChevronDown,
  FileText,
  LogOut,
  Menu as MenuIcon,
  Monitor,
  Settings,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import { API_IMAGE_URL, CRYPTO_TOKEN_SECRET_KEY } from "../../config";
import { useSidebar } from "../ContextAPI/SidebarContext";
import { getUserData } from "../../Actions/common";
import { getRoleByPermissionAction } from "../../Actions/RoleActions";
import logo from "../../Assets/svg/master-logo-black.png";
import DropdownNotification from "../../Sections/Notification/SocketNotification";
import ApprovalBell from "../../Sections/Approval/ApprovalBell";
import SideBarComponent from "../Common/Sidebar";
import { useUser } from "../ContextAPI/ProfileContext";
import { useSidebarDesk } from "../ContextAPI/SidebarContextDesktop";
import CustomSnackbar from "../../Sections/Common/snackBar";
import "./Header.css";
import HeaderGlobalSearch from "./HeaderGlobalSearch";

// const PAGE_TITLES = [
//   ["/dashboard", "Dashboard"],
//   ["/tenant", "Tenant Users"],
//   ["/transactions", "Transactions"],
//   ["/reports", "Reports"],
//   ["/plan-management", "Plan Management"],
//   ["/plan-feature-management", "Plan Features"],
//   ["/country-document", "KYC Country Document"],
//   ["/documentation-type", "KYC Country Document Type"],
//   ["/invoices", "Automated Invoices"],
//   ["/users", "User Management"],
//   ["/role-manager", "Role Management"],
//   ["/audit-trail", "Audit Trail"],
//   ["/mail-logs", "Email Logs"],
//   ["/master-tenant-logs", "Master Tenant Logs"],
//   ["/support-ticket", "Ticketing System"],
//   ["/profile", "Profile"],
//   ["/settings", "Settings"],
//   ["/approvals", "Approval Requests"],
//   ["/notifications", "Notifications"],
// ];

// const getPageTitle = (pathname) =>
//   PAGE_TITLES.find(([path]) => pathname === path || pathname.startsWith(`${path}/`))?.[1] ||
//   "Master Portal";

const getPermissionData = (key) => {
  const decryptedData = CryptoJS.AES.decrypt(
    localStorage.getItem(key),
    CRYPTO_TOKEN_SECRET_KEY
  ).toString(CryptoJS.enc.Utf8);
  return JSON.parse(decryptedData);
};

const readCachedPermissions = (userId) => {
  try {
    if (userId && localStorage.getItem(`${userId}_permissionData`)) {
      const cached = getPermissionData(`${userId}_permissionData`);
      return Array.isArray(cached) ? cached : [];
    }
  } catch (e) {
    console.error("Error reading cached permissions", e);
  }
  return [];
};

/** Normalize plain + encrypted interceptor shapes for role permission payloads. */
const unwrapPermissionPayload = (res) => {
  const body = res?.data?.data ?? res?.data ?? res;
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;
  return [];
};

const SidebarSkeleton = ({ isSandbox }) => (
  <div
    id="kt_aside"
    className={`mp-sidebar aside drawer drawer-start ${isSandbox ? "sandbox" : ""}`}
    aria-busy="true"
    aria-label="Loading navigation"
  >
    <div className="aside-menu flex-column-fluid" id="kt_aside_menu">
      <nav className="mp-sidebar-nav">
        <ul className="mp-nav-list list-unstyled" style={{ padding: "18px 14px", gap: 10 }}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <li
              key={i}
              style={{
                height: 42,
                borderRadius: 12,
                marginBottom: 8,
                background: "rgba(255,255,255,0.12)",
                animation: "mp-sidebar-pulse 1.2s ease-in-out infinite",
                animationDelay: `${i * 0.08}s`,
              }}
            />
          ))}
        </ul>
      </nav>
    </div>
  </div>
);

const HeaderComponent = ({ sandBoxEvent }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { toggleSidebar } = useSidebar();
  const { userDetails } = useUser();
  const [isSandbox, setIsSandbox] = useState(() => {
    const stored = localStorage.getItem("isSandbox");
    return stored === null ? false : stored === "true";
  });
  const { sidebarDeskOpen, toggleSidebarDesk } = useSidebarDesk();
  const userData = useMemo(
    () => (localStorage.getItem("userData") ? getUserData() : null),
    // Re-read only when identity changes; getUserData decrypt is expensive
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const UserDetailData = userDetails?.data || userDetails;
  const location = useLocation();
  // const pageTitle = getPageTitle(location.pathname);
  const displayName =
    [UserDetailData?.firstName, UserDetailData?.lastName].filter(Boolean).join(" ") ||
    "Your account";

  const [permission, setPermission] = useState(() =>
    readCachedPermissions(userData?.id)
  );
  // Only block UI when we have nothing cached — otherwise show sidebar immediately
  const [permissionLoading, setPermissionLoading] = useState(
    () => readCachedPermissions(userData?.id).length === 0
  );

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const ipWhiteListPermission = permission[0]?.role_permissions?.find(
    (m) => m?.module && m?.module?.slug === "ip-whitelist"
  )?.canView;

  const templatePermission = permission[0]?.role_permissions?.find(
    (m) => m?.module && m?.module?.slug === "templates"
  )?.canView;

  const notifySandbox = useCallback(() => {
    if (typeof sandBoxEvent === "function") sandBoxEvent();
  }, [sandBoxEvent]);

  useEffect(() => {
    localStorage.setItem("isSandbox", isSandbox);
    notifySandbox();
  }, [isSandbox, notifySandbox]);

  const handleSwitchEnvironment = () => {
    setIsSandbox((prev) => !prev);
  };

  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    const loadPermissions = async () => {
      if (!userData?.roleId) {
        setPermissionLoading(false);
        return;
      }

      const hasCache = permission.length > 0;
      // Keep showing cached sidebar; only spinner if cold start
      if (!hasCache) setPermissionLoading(true);

      try {
        const data = await getRoleByPermissionAction(userData.roleId);
        const permissionData = unwrapPermissionPayload(data);

        if (cancelled) return;

        if (permissionData.length > 0) {
          const encryptedPermissionData = CryptoJS.AES.encrypt(
            JSON.stringify(permissionData),
            CRYPTO_TOKEN_SECRET_KEY
          ).toString();

          localStorage.setItem(
            `${userData.id}_permissionData`,
            encryptedPermissionData
          );
          setPermission(permissionData);
        }
      } catch (error) {
        if (cancelled) return;
        // Keep cached permissions on failure — don't blank the sidebar
        let message = error?.message || "Failed to refresh permissions.";
        try {
          const parsed = error.response?.data
            ? typeof error.response.data === "string"
              ? JSON.parse(error.response.data)
              : error.response.data
            : null;
          if (parsed?.message) message = parsed.message;
        } catch (_) {
          /* ignore parse errors */
        }

        if (!hasCache) {
          setSnackbar({
            open: true,
            message,
            severity: "error",
          });
        }
      } finally {
        if (!cancelled) setPermissionLoading(false);
      }
    };

    loadPermissions();
    return () => {
      cancelled = true;
    };
    // Fresh fetch once per user session mount — cache paints first
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id, userData?.roleId]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileRoutes = (routeName) => {
    if (routeName === "profile") {
      navigate(`/profile`);
    }

    if (routeName === "settings") {
      navigate(`/settings`);
    }

    if (routeName === "login-sessions") {
      navigate(`/login-sessions`);
    }

    if (routeName === "whitelist-ips") {
      navigate(`/whitelist-ips`);
    }

    if (routeName === "templates") {
      navigate(`/templates`);
    }

    if (routeName === "terms-conditions") {
      navigate(`/terms-conditions`);
    }

    setAnchorEl(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("GLOBAL_SETTING");
    localStorage.removeItem("userData");
    localStorage.removeItem(`${userData?.id}_permissionData`);
    setAnchorEl(null);
    setSnackbar({
      open: true,
      message: "Logout successfully.",
      severity: "success",
    });
    setTimeout(() => {
      navigate("/login");
    }, 1000);
  };

  const stringAvatar = (profileData) => {
    const firstNameInitial = profileData?.firstName
      ? profileData.firstName.split("")[0]
      : "";
    const lastNameInitial = profileData?.lastName
      ? profileData.lastName.split("")[0]
      : "";

    return {
      children: `${firstNameInitial}${lastNameInitial}`,
    };
  };

  const showSidebar = permission.length > 0;
  const showSidebarSkeleton = permissionLoading && !showSidebar;

  return (
    <>
      {isSandbox && (
        <div
          style={{
            width: "100%",
            background: "#89a6ff",
            color: "#fff",
            padding: "6px 32px",
            textAlign: "center",
            fontWeight: 500,
            fontSize: "16px",
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: 2000,
            borderBottom: "0px solid #fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          <span
            style={{
              display: "flex",
              alignItems: "center",
              marginRight: 5,
            }}
          >
            <i
              className="fa-solid fa-flask"
              style={{
                color: "#fff",
                fontSize: 20,
                marginRight: 8,
              }}
            ></i>
          </span>
          <span>
            You are in the sandbox environment.{" "}
            <span
              style={{
                color: "#22419e",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={handleSwitchEnvironment}
            >
              Switch to live environment
            </span>
          </span>
        </div>
      )}

      <div id="kt_header" className={isSandbox ? "header sandbox" : "header"}>
        <div className="mp-header-shell">
          <div className="mp-header-left">
            <button
              type="button"
              className="mp-header-mobile-menu d-xl-none"
              aria-label="Open navigation"
              title="Open navigation"
              onClick={(event) => {
                toggleSidebar(event);
                toggleSidebarDesk(!sidebarDeskOpen);
              }}
            >
              <MenuIcon size={20} aria-hidden />
            </button>

            <Link to="/dashboard" className="mp-header-brand" aria-label="Go to dashboard">
              <img src={logo} alt="QBot" className="mp-header-logo" />
            </Link>

            <span className="mp-header-divider" aria-hidden />

      
          </div>

          <HeaderGlobalSearch permission={permission} />

          <div className="mp-header-actions">
            <div className="mp-header-icon-actions">
              <ApprovalBell />
              <DropdownNotification />
            </div>

            <button
              type="button"
              className="mp-header-profile"
              onClick={handleClick}
              aria-controls={anchorEl ? "user-profile-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={Boolean(anchorEl)}
            >
              <Avatar
                {...stringAvatar(UserDetailData)}
                src={
                  UserDetailData?.profilePic
                    ? `${API_IMAGE_URL}/qbotmasterapp.netcluesdemo.com/profilePics/${UserDetailData.profilePic}`
                    : undefined
                }
                className="mp-header-avatar"
              />
              <span className="mp-header-profile-copy">
                <strong>{displayName}</strong>
                <small>{UserDetailData?.email || "Profile & settings"}</small>
              </span>
              <ChevronDown
                size={16}
                className={anchorEl ? "is-open" : ""}
                aria-hidden
              />
            </button>

            <Menu
              className="mp-profile-menu"
              id="user-profile-menu"
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => handleProfileRoutes()}
              disableScrollLock
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                className: "mp-profile-menu-paper",
                elevation: 0,
              }}
            >
              <div className="mp-profile-menu-header">
                <Avatar
                  {...stringAvatar(UserDetailData)}
                  src={
                    UserDetailData?.profilePic
                      ? `${API_IMAGE_URL}/qbotmasterapp.netcluesdemo.com/profilePics/${UserDetailData.profilePic}`
                      : undefined
                  }
                />
                <div>
                  <strong>{displayName}</strong>
                  <span>{UserDetailData?.email || "Master Portal account"}</span>
                </div>
              </div>

              <MenuItem className="mp-profile-menu-item" onClick={() => handleProfileRoutes("profile")}>
                <UserRound size={17} aria-hidden />
                Profile
              </MenuItem>
              {ipWhiteListPermission ? (
                <MenuItem className="mp-profile-menu-item" onClick={() => handleProfileRoutes("whitelist-ips")}>
                  <ShieldCheck size={17} aria-hidden />
                  Whitelist IPs
                </MenuItem>
              ) : null}
              {templatePermission ? (
                <MenuItem className="mp-profile-menu-item" onClick={() => handleProfileRoutes("templates")}>
                  <FileText size={17} aria-hidden />
                  Email Templates
                </MenuItem>
              ) : null}
              <MenuItem className="mp-profile-menu-item" onClick={() => handleProfileRoutes("settings")}>
                <Settings size={17} aria-hidden />
                Settings
              </MenuItem>
              <MenuItem className="mp-profile-menu-item" onClick={() => handleProfileRoutes("login-sessions")}>
                <Monitor size={17} aria-hidden />
                Login Sessions
              </MenuItem>
              <MenuItem className="mp-profile-menu-item" onClick={() => handleProfileRoutes("terms-conditions")}>
                <FileText size={17} aria-hidden />
                Terms & Conditions
              </MenuItem>
              <div className="mp-profile-menu-separator" />
              <MenuItem className="mp-profile-menu-item is-danger" onClick={handleLogout}>
                <LogOut size={17} aria-hidden />
                Logout
              </MenuItem>
            </Menu>
          </div>
        </div>

        <CustomSnackbar
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={handleCloseSnackbar}
        />
      </div>
      {showSidebar ? (
        <SideBarComponent isSandbox={isSandbox} permission={permission} />
      ) : showSidebarSkeleton ? (
        <SidebarSkeleton isSandbox={isSandbox} />
      ) : null}
    </>
  );
};

export default HeaderComponent;
